import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/utils/apiClient';
import { Trash2, RefreshCw, ExternalLink, HardDrive } from 'lucide-react';
import LoadingSpinner from '@/components/LoadingSpinner';

interface SharedSession {
  session_id: string;
  session_title: string;
  practitioner_id: string;
  practitioner_name: string;
  share_token: string;
  total_plays: number;
  unique_listeners: number;
  created_at: string;
  last_accessed_at: string | null;
  has_mixed_audio: boolean;
  storage_used_mb: number;
}

export function AdminSharedSessionsPage() {
  const { toast } = useToast();
  const [sessions, setSessions] = useState<SharedSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  useEffect(() => {
    fetchSharedSessions();
  }, []);

  const fetchSharedSessions = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/sessions/admin/shared-sessions/active?limit=100', {
        includeAuth: true
      });
      setSessions(response.shared_sessions);
      setTotalCount(response.total_count);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to load shared sessions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const revokeSession = async (sessionId: string, sessionTitle: string) => {
    if (!confirm(`Are you sure you want to revoke the share link for "${sessionTitle}"?`)) return;

    setRevokingId(sessionId);
    try {
      const response = await apiClient.post(`/sessions/admin/shared-sessions/${sessionId}/revoke`, {}, {
        includeAuth: true
      });

      toast({
        title: "Link Revoked",
        description: `Freed ${response.storage_freed_mb} MB of storage.`,
      });

      // Remove from list
      setSessions(sessions.filter(s => s.session_id !== sessionId));
      setTotalCount(prev => prev - 1);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to revoke link",
        variant: "destructive",
      });
    } finally {
      setRevokingId(null);
    }
  };

  const getTotalStorage = () => {
    return sessions.reduce((total, session) => total + session.storage_used_mb, 0).toFixed(2);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <LoadingSpinner />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Shared Sessions Management</h1>
          <p className="text-muted-foreground mt-1">Manage all active shared session links</p>
        </div>
        <Button onClick={fetchSharedSessions} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Active Links</CardDescription>
            <CardTitle className="text-2xl">{totalCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Storage Used</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <HardDrive className="h-5 w-5" />
              {getTotalStorage()} MB
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Plays</CardDescription>
            <CardTitle className="text-2xl">
              {sessions.reduce((total, s) => total + s.total_plays, 0).toLocaleString()}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Sessions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Active Shared Sessions</CardTitle>
          <CardDescription>
            All currently active share links across all practitioners
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <Alert>
              <AlertDescription>
                No active shared sessions found.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Session Title</TableHead>
                    <TableHead>Practitioner</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-center">Plays</TableHead>
                    <TableHead className="text-center">Storage</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.session_id}>
                      <TableCell className="font-medium">
                        <div className="max-w-xs truncate">{session.session_title}</div>
                        {session.has_mixed_audio && (
                          <Badge variant="secondary" className="mt-1 text-xs">
                            Pre-mixed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs truncate">{session.practitioner_name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {session.practitioner_id}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>{formatDate(session.created_at)}</div>
                        {session.last_accessed_at && (
                          <div className="text-xs text-muted-foreground">
                            Last: {formatDate(session.last_accessed_at)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{session.total_plays}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {session.storage_used_mb > 0 ? (
                          <span className="text-sm">{session.storage_used_mb.toFixed(2)} MB</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`${window.location.origin}/listen/${session.share_token}`, '_blank')}
                            title="Open share link"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => revokeSession(session.session_id, session.session_title)}
                            disabled={revokingId === session.session_id}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            {revokingId === session.session_id ? 'Revoking...' : 'Revoke'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
